import {
	currentTargetRequired,
	qsOptional,
	qsRequired,
	removeTrapFocus,
	targetClosestRequired,
	trapFocus,
	debounce,
	getViewport
} from '../core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'


export class DetailsModal extends UcoastEl {
	static htmlSelector = 'details-modal'
	isTooltip: boolean
	onBodyClickEvent?: (event: Event) => void

	elements: {
		detailsContainer: HTMLDetailsElement
		summaryToggle: HTMLElement
		closeButton?: HTMLButtonElement
		content: HTMLElement
	}

	constructor() {
		super()
		this.isTooltip = this.getAttribute('data-tooltip') === 'true'
		this.elements = this.getElements()
	}

	getElements() {
		const detailsContainer = qsRequired<HTMLDetailsElement>('details', this)
		const summaryToggle = qsRequired('summary', this)
		const closeButton = qsOptional<HTMLButtonElement>('button', this)
		const content = qsRequired('[data-modal-content]', this)
		return {
			detailsContainer,
			summaryToggle,
			closeButton,
			content,
		}
	}

	override connectedCallback() {
		this.elements = this.getElements()

		this.elements.detailsContainer.addEventListener(
			'keyup',
			(event) =>
				event.code.toUpperCase() === 'ESCAPE' &&
				this.close(this.elements.summaryToggle)
		)
		this.elements.summaryToggle.addEventListener(
			'click',
			this.onSummaryClick.bind(this)
		)

		if (this.isTooltip) {
			const tooltipResizeListener = debounce(this.setTooltipEvents.bind(this), 800);
			window.addEventListener('resize', tooltipResizeListener);
			this.setTooltipEvents();
		} else if (this.elements.closeButton) {
			this.elements.closeButton.addEventListener('click', (_) =>
				this.close(this.elements.summaryToggle)
			)
			this.elements.closeButton.addEventListener(
				'click',
				this.close.bind(this)
			)
		}

		this.elements.summaryToggle.setAttribute('role', 'button')
	}

	setTooltipEvents() {
		const summaryClick = this.onSummaryClick.bind(this);
		const close = () => this.close(this.elements.summaryToggle)


		if(getViewport() === 'm-up') {
			this.elements.summaryToggle.addEventListener(
				'mouseenter',
				summaryClick
			)
			this.elements.summaryToggle.addEventListener(
				'mouseleave',
				close
			)
		} else {
			this.elements.summaryToggle.removeEventListener(
				'mouseleave',
				close
			)
			this.elements.summaryToggle.removeEventListener(
				'mouseenter',
				summaryClick
			)
		}
	}

	isOpen() {
		return this.elements.detailsContainer.hasAttribute('open')
	}

	onSummaryClick(event: Event) {
		event.preventDefault()
		event.stopImmediatePropagation();
		// console.log({ event })
		if (!this.isOpen()) {
			this.open(event)
		} else {
			if (event.type !== 'mouseenter' && event.type !== 'keydown') {
				this.close(this.elements.summaryToggle)
			}
		}
	}

	onBodyClick(event: Event) {
		const currentTarget = currentTargetRequired(event)
		if (
			!this.contains(currentTarget) ||
			currentTarget.getAttribute('data-overlay') === 'true'
		)
			this.close(this.elements.summaryToggle)
	}

	open(event: Event) {
		const details: HTMLDetailsElement = targetClosestRequired(
			event,
			'details'
		)
		this.onBodyClickEvent =
			this.onBodyClickEvent || this.onBodyClick.bind(this)
		details.setAttribute('open', 'true')
		if (!this.onBodyClickEvent) {
			throw new Error('no onBodyClickEvent')
		}
		document.body.addEventListener('click', this.onBodyClickEvent)
		if (!this.isTooltip) {
			document.body.classList.add('overflow-hidden')
		}

		try {
			trapFocus(this.elements.content)
		} catch (e) {
			console.log('no tabindex')
		}
	}

	close(focusToggle: HTMLElement | undefined) {
		removeTrapFocus(focusToggle ? focusToggle : this.elements.summaryToggle)
		this.elements.detailsContainer.removeAttribute('open')
		if (this.onBodyClickEvent) {
			document.body.removeEventListener('click', this.onBodyClickEvent)
		}

		if (!this.isTooltip) {
			document.body.classList.remove('overflow-hidden')
		}
	}
}
